import "./PRmanager.css";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { Table } from "react-bootstrap";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Select from "react-select";
import Instance from "../../Utils/Axios";
import { showToast } from "../../Utils";
import { useParams, useNavigate } from "react-router-dom";
import useKeyScroll from "../../Hooks/useKeyScroll";
import ReactQuill from "react-quill";
import {
  normalizeHtmlValue,
  quillFormats,
} from "./quillConfig";

export default function PRview() {
  const scrollContainerRef = useKeyScroll();
  const { id } = useParams();
  const navigate = useNavigate();

  const [editorData, setEditorData] = useState("");
  const [company, setCompany] = useState({
    name: "",
    contactPerson: "",
    address: "",
    mobile: "",
    email: "",
    website: "",
    state: "",
    country: "",
    city: "",
    isdCode: "",
  });
  const [formData, setFormData] = useState({
    packageId: "",
    packageMemberId: "",
    title: "",
    description: "",
    showContactDetails: "",
    image: "",
    addNote: "",
    status: "",
    categoryId: "",
    packageName: "",
    categoryName: "",
  });
  const [selectedImagesData, setSelectedImagesData] = useState([]);
  const [packageList, setPackageList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [imagesList, setImagesList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [companyId, setCompanyId] = useState("");

  const resolveCountry = useCallback(
    (countryValue, fallbackIsdCode) => {
      const input = countryValue === undefined || countryValue === null ? "" : String(countryValue).trim();
      const fallback = fallbackIsdCode === undefined || fallbackIsdCode === null ? "" : String(fallbackIsdCode).trim();
      const inputLower = input.toLowerCase();

      let countryMatch =
        countryList.find((c) => String(c.countryCode) === input) ||
        countryList.find((c) => String(c.countryName).trim().toLowerCase() === inputLower) ||
        countryList.find((c) => String(c.countryId) === input) ||
        null;

      let finalIsdCode = fallback;

      if (fallback) {
        const cleanFallback = fallback.replace("+", "");
        const isdMatch = countryList.find(
          (c) => String(c.isdCode || "").replace("+", "") === cleanFallback
        );
        if (isdMatch) {
          finalIsdCode = String(isdMatch.isdCode);
        }
      } else if (countryMatch && countryMatch.isdCode) {
        finalIsdCode = String(countryMatch.isdCode);
      }

      return {
        country: countryMatch ? String(countryMatch.countryCode) : input,
        isdCode: finalIsdCode,
      };
    },
    [countryList],
  );

  const fetchCompanies = useCallback((compId) => {
    const payload = compId ? { companyId: compId } : {};
    Instance.post("/user/usercompany/getList", payload)
      .then((response) => {
        if (response.data.resCode === "200") setCompanyList(response.data.message || []);
      })
      .catch((error) => console.error("Error fetching company list:", error));
  }, []);

  const fetchPackages = useCallback(() => {
    Instance.post("/user/credit/list", { status: "ACTIVE", orderBy: "availablePR", orderByType: "DESC", availablePR: "1", })
      .then((r) => { if (r.data.resCode === "200") setPackageList(r.data.message || []); })
      .catch((e) => console.error("Error fetching package list:", e));
  }, []);

  const fetchCategories = useCallback(() => {
    Instance.post("/user/category/getList", { status: "ACTIVE" })
      .then((r) => { if (r.data.resCode === "200") setCategoryList(r.data.message || []); })
      .catch((e) => console.error("Error fetching category list:", e));
  }, []);

  const fetchGallery = useCallback(() => {
    Instance.post("/user/gallery/getList", { docType: "supportingDocument" })
      .then((r) => { if (r.data.resCode === "200") setImagesList(r.data.message || []); })
      .catch((e) => console.error("Error fetching gallery list:", e));
  }, []);

  const fetchCountryList = useCallback(() => {
    Instance.post("/user/common/countryList", { status: "" })
      .then((r) => {
        if (r.data.resCode === "200") {
          setCountryList((r.data.message || []).filter((c) => String(c.countryName || "").trim() !== ""));
        }
      })
      .catch((e) => console.error("Error fetching countries list:", e));
  }, []);

  useEffect(() => {
    fetchPackages();
    fetchCategories();
    fetchGallery();
    fetchCountryList();
    if (id) fetchCompanies();
  }, [fetchPackages, fetchCategories, fetchGallery, fetchCountryList, fetchCompanies, id]);

  useEffect(() => {
    if (!id) return;
    Instance.post("/user/pressrelease/details", { id })
      .then(async ({ data }) => {
        if (data?.resCode !== "200") return;
        const item = data.message;

        setEditorData(item.description || "");

        setFormData({
          packageId: item.packageId ? String(item.packageId) : "",
          packageMemberId: item.packageMemberId ? String(item.packageMemberId) : "",
          title: item.title || "",
          description: item.description || "",
          showContactDetails: item.showContactDetails || "NO",
          image: item.image || "",
          addNote: item.addNote || "",
          status: item.status || "",
          categoryId: item.categoryId ? String(item.categoryId) : "",
          packageName: item.packageName || "",
          categoryName: item.categoryName || "",
        });

        // ── Company ──
        const prCompanyId = item.companyId ? String(item.companyId) : "";
        if (prCompanyId) {
          setCompanyId(prCompanyId);
          // Fetch company data
          try {
            const compRes = await Instance.post("/user/usercompany/getList", { companyId: prCompanyId });
            let compListData = compRes?.data?.resCode === "200" ? compRes.data.message || [] : [];
            if (!Array.isArray(compListData)) compListData = [compListData];
            setCompanyList(compListData);
            const validCompany = compListData.find((c) => String(c.companyId) === prCompanyId);
            if (validCompany) {
              const resolved = resolveCountry(validCompany.country, validCompany.isdCode);
              setCompany({
                name: validCompany.name || "",
                contactPerson: validCompany.contactPerson || "",
                address: validCompany.address || "",
                mobile: validCompany.mobile || "",
                email: validCompany.email || "",
                website: validCompany.website || "",
                state: validCompany.state || "",
                country: resolved.country || "",
                city: validCompany.city || "",
                isdCode: resolved.isdCode || "",
              });
            }
          } catch (e) {
            console.error("Failed to fetch company:", e);
          }
        } else {
          setCompanyId("");
          const resolved = resolveCountry(item.country, item.isdCode || item.phoneIsdCode);
          setCompany({
            name: item.companyName || "",
            contactPerson: item.contactPerson || "",
            address: item.companyAddress || "",
            mobile: item.mobile || "",
            email: item.email || "",
            website: item.companyWebsite || "",
            state: item.state || "",
            country: resolved.country || "",
            city: item.city || "",
            isdCode: resolved.isdCode || "",
          });
        }

        // ── Images ──
        if (item.image) {
          const savedIds = String(item.image).split("|").map((x) => x.trim()).filter(Boolean);
          try {
            const gallRes = await Instance.post("/user/gallery/getList", { docType: "supportingDocument" });
            const gallData = gallRes?.data?.resCode === "200" ? gallRes.data.message || [] : [];
            setImagesList(gallData);
            const matched = gallData.filter((img) => savedIds.includes(String(img.galleryId)));
            const missingIds = savedIds.filter((sid) => !matched.some((img) => String(img.galleryId) === sid));
            if (missingIds.length > 0) {
              const results = await Promise.all(
                missingIds.map(async (galleryId) => {
                  const { data: d } = await Instance.post("/user/gallery/details", { galleryId });
                  return d?.resCode === "200" ? d.message : null;
                }),
              );
              setSelectedImagesData([...matched, ...results.filter(Boolean)]);
            } else {
              setSelectedImagesData(matched);
            }
          } catch (e) {
            console.error("Failed to fetch gallery:", e);
          }
        }
      })
      .catch((e) => console.error("Failed to fetch PR details:", e));
  }, [id]);

  const formatSize = (size) =>
    !size ? "-" : size > 1024 * 1024
      ? `${(size / (1024 * 1024)).toFixed(2)} MB`
      : `${(size / 1024).toFixed(2)} KB`;

  const copyURL = async (url) => {
    const textToCopy = typeof url === "string" ? url : String(url ?? "");
    if (!textToCopy) { showToast("Nothing to copy.", "error", "error"); return; }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
        showToast("URL copied!", "success", "success");
        return;
      }
      const textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (copied) showToast("URL copied!", "success", "success");
      else showToast("Copy failed. Please copy manually.", "error", "error");
    } catch {
      showToast("Copy failed. Please copy manually.", "error", "error");
    }
  };

  const packageOptions = packageList.map((list) => ({
    value: String(list.packageMemberId),
    label: `${list.packageName} (Credit Left: ${Number(list.availablePR)} | Expiry: ${list.expiryDate
      ? new Date(list.expiryDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      })
      : ''
      })`,
  }));

  const categoryOptions = categoryList.map((cat) => ({
    value: String(cat.categoryId),
    label: cat.name || String(cat.categoryId),
  }));

  const galleryOptions = imagesList.map((img) => ({
    value: String(img.galleryId),
    label: img.name || String(img.galleryId),
  }));

  const selectedGalleryOptions = galleryOptions.filter((opt) =>
    formData.image
      ? formData.image.split("|").map((x) => x.trim()).includes(String(opt.value))
      : false,
  );

  const countryOptions = countryList.map((c) => ({
    value: c.countryCode,
    label: c.countryName,
    displayLabel: c.countryName,
    countryCode: c.countryCode,
    isdCode: c.isdCode,
  }));

  const isdOptions = (() => {
    const seen = new Set();
    const opts = [];
    for (const c of countryList) {
      const code = c.isdCode;
      if (!code || seen.has(code)) continue;
      seen.add(code);
      opts.push({
        value: code,
        label: `${c.countryName} ${code}`,
        displayLabel: code,
        countryCode: c.countryCode,
      });
    }
    return opts;
  })();

  // Read-only select styles
  const readOnlySelectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "#e9ecef",
      pointerEvents: "none",
      borderColor: "#dee2e6",
      boxShadow: "none",
    }),
    singleValue: (base) => ({ ...base, color: "#495057" }),
    multiValue: (base) => ({ ...base, backgroundColor: "#dee2e6" }),
    multiValueLabel: (base) => ({ ...base, color: "#495057" }),
    multiValueRemove: (base) => ({ ...base, display: "none" }),
    dropdownIndicator: (base) => ({ ...base, display: "none" }),
    indicatorSeparator: (base) => ({ ...base, display: "none" }),
    clearIndicator: (base) => ({ ...base, display: "none" }),
  };

  return (
    <>
      <div className="pr_preview_container">
        <div
          className="d-flex align-items-center justify-content-between my-3"
          style={{ marginRight: "30px" }}
        >
          <div />
          <button
            className="back_btn_stepper "
            onClick={() => navigate("/customer/pr-manager")}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back
          </button>
        </div>

        <div className="pr-form-wrapper bg-white shadow rounded">
          <div className="pr_form_editor">
            <Form className="pr_form_text">

              {/* ── Package & Category ── */}
              <Form.Group className="mb-4">
                <Form.Label>Choose Package</Form.Label>
                <Select
                  className="submit_form_select"
                  classNamePrefix="react-select"
                  options={packageOptions}
                  value={
                    packageOptions.find((o) => o.value === String(formData.packageMemberId)) ||
                    (formData.packageName ? { value: String(formData.packageMemberId), label: formData.packageName } : null)
                  }
                  placeholder="Select from dropdown"
                  isDisabled
                  styles={readOnlySelectStyles}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Select Categories</Form.Label>
                <Select
                  className="submit_form_select"
                  classNamePrefix="react-select"
                  options={categoryOptions}
                  value={categoryOptions.find((o) => o.value === String(formData.categoryId)) || null}
                  placeholder="Select from dropdown"
                  isDisabled
                  styles={readOnlySelectStyles}
                />
              </Form.Group>

              {/* ── Headline ── */}
              <Form.Group className="mb-4">
                <Form.Label>Headline</Form.Label>
                <Form.Control
                  type="text"
                  className="submit_form_control"
                  value={formData.title}
                  readOnly
                  plaintext={false}
                />
              </Form.Group>

              {/* ── PR Body Preview ── */}
              <Form.Group className="mb-4">
                <Form.Label>Press Release Body</Form.Label>
                <div
                  className="custom-ckeditor-wrapper pr-preview"
                  style={{
                    border: "1px solid #9491DF",
                    borderRadius: "8px",
                    padding: "0.5rem 1rem",
                    backgroundColor: "#F8F9FA",
                    minHeight: "150px"
                  }}
                >
                  {!normalizeHtmlValue(editorData) && (
                    <div className="text-muted mb-2">No press release body to preview.</div>
                  )}
                  <ReactQuill
                    value={normalizeHtmlValue(editorData)}
                    modules={{ toolbar: false }}
                    formats={quillFormats}
                    bounds={".custom-ckeditor-wrapper"}
                    readOnly
                    theme="snow"
                  />
                </div>
              </Form.Group>

              {/* ── Supporting Documents ── */}
              <Form.Group className="mb-4">
                <Form.Label>Supporting Documents</Form.Label>
                <Select
                  className="submit_form_select"
                  classNamePrefix="react-select"
                  isMulti
                  options={galleryOptions}
                  value={selectedGalleryOptions}
                  isDisabled
                  styles={readOnlySelectStyles}
                />
              </Form.Group>

              {selectedImagesData.length > 0 && (
                <div
                  className="mb-4"
                  style={{ border: "1px solid #dee2e6", borderRadius: "8px", overflow: "hidden" }}
                >
                  <div
                    style={{ overflowX: "auto", width: "100%", outline: "none" }}
                    ref={scrollContainerRef}
                    tabIndex="0"
                  >
                    <Table
                      className="pr_support_docs_table mb-0"
                      style={{ tableLayout: "fixed", width: "100%", margin: 0 }}
                    >
                      <thead className="pr_tables_head" style={{ backgroundColor: "#f8f9fa" }}>
                        <tr>
                          {["Document Name", "Document URL", "Size", "Copy"].map((h, i) => (
                            <th
                              key={h}
                              style={{
                                width: ["30%", "35%", "15%", "20%"][i],
                                padding: "12px 16px",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                color: "#495057",
                                borderBottom: "2px solid #dee2e6",
                                textAlign: i === 3 ? "center" : undefined,
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="pr_tables_body">
                        {selectedImagesData.map((detail, index) => (
                          <tr key={index} style={{ borderBottom: "1px solid #dee2e6" }}>
                            <td style={{ padding: "12px 16px", wordBreak: "break-word", overflowWrap: "break-word", verticalAlign: "middle" }}>
                              {detail.name}
                            </td>
                            <td style={{ padding: "12px 16px", wordBreak: "break-word", overflowWrap: "break-word", verticalAlign: "middle", fontSize: "0.82rem", color: "#555" }}>
                              {detail.documentUrl || detail.imgUrl}
                            </td>
                            <td style={{ padding: "12px 16px", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                              {formatSize(detail.size)}
                            </td>
                            <td style={{ padding: "12px 16px", verticalAlign: "middle", textAlign: "center" }}>
                              <button
                                className="pr_form_next_btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  copyURL("https://pr.webandapi.com" + (detail.documentUrl || detail.imgUrl));
                                }}
                              >
                                Copy
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}

              {/* ── Sub-account ── */}
              <Form.Group className="mb-4">
                <Form.Label>Select Sub-account</Form.Label>
                <Select
                  className="submit_form_select"
                  classNamePrefix="react-select"
                  options={[
                    { value: "ADD_NEW", label: "Add new company" },
                    ...companyList.map((comp) => ({
                      value: String(comp.companyId),
                      label: comp.name || String(comp.companyId),
                    }))
                  ]}
                  value={companyId
                    ? [
                      { value: "ADD_NEW", label: "Add new company" },
                      ...companyList.map((comp) => ({
                        value: String(comp.companyId),
                        label: comp.name || String(comp.companyId),
                      }))
                    ].find((opt) => opt.value === String(companyId)) || null
                    : null
                  }
                  placeholder="Select Company"
                  isDisabled
                  styles={readOnlySelectStyles}
                />
              </Form.Group>

              {formData.showContactDetails === "YES" && (
                <>
                  <h4 className="pr_or">OR</h4>

                  {/* ── Company Details ── */}
                  <Row>
                    <Col className="custom_col_submit mb-4">
                      <Form.Label>Company Name</Form.Label>
                      <Form.Control
                        placeholder="Enter Company Name"
                        className="submit_form_control"
                        value={company.name}
                        readOnly
                      />
                    </Col>
                    <Col className="custom_col_submit mb-4">
                      <Form.Label>Contact Person Name</Form.Label>
                      <Form.Control
                        placeholder="Enter Person Name"
                        className="submit_form_control"
                        value={company.contactPerson}
                        readOnly
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col className="custom_col_submit mb-4">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        placeholder="Enter Email"
                        className="submit_form_control"
                        value={company.email}
                        readOnly
                      />
                    </Col>
                    <Col className="custom_col_submit mb-4">
                      <Form.Label>Phone Number</Form.Label>
                      <div className="phone_input_wrapper">
                        <div className="isd_code_dropdown">
                          <Select
                            options={isdOptions}
                            placeholder="+91"
                            isDisabled
                            isSearchable={false}
                            classNamePrefix="react-isd-select"
                            menuPortalTarget={document.body}
                            value={isdOptions.find((o) => o.value === company.isdCode) || null}
                            formatOptionLabel={({ countryCode, displayLabel }) => (
                              <div className="d-flex align-items-center">
                                {countryCode && (
                                  <img
                                    src={`https://flagcdn.com/w20/${String(countryCode).toLowerCase()}.png`}
                                    alt=""
                                    className="me-2"
                                    style={{ width: "15px", height: "auto" }}
                                  />
                                )}
                                <span>{displayLabel}</span>
                              </div>
                            )}
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderRadius: "8px 0 0 8px",
                                borderColor: "transparent",
                                backgroundColor: "#e9ecef",
                                boxShadow: "none",
                                "&:hover": { borderColor: "transparent" },
                                minHeight: "48px",
                                borderRight: "none",
                              }),
                              dropdownIndicator: (base) => ({ ...base, display: "none" }),
                              indicatorSeparator: (base) => ({ ...base, display: "none" }),
                              singleValue: (base) => ({ ...base, color: "#495057" }),
                              menu: (base) => ({ ...base, zIndex: 9999, width: "150px" }),
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </div>
                        <div className="phone_number_input">
                          <Form.Control
                            placeholder="Enter Phone Number"
                            className="submit_form_control"
                            value={company.mobile}
                            readOnly
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col className="custom_col_submit mb-4">
                      <Form.Label>Company Website</Form.Label>
                      <Form.Control
                        placeholder="Enter Website URL"
                        className="submit_form_control"
                        value={company.website}
                        readOnly
                      />
                    </Col>
                    <Col className="custom_col_submit mb-4">
                      <Form.Label>Company Address</Form.Label>
                      <Form.Control
                        placeholder="Enter Address"
                        className="submit_form_control"
                        value={company.address}
                        readOnly
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs="3" className="custom_col_submit mb-4">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        placeholder="Enter City"
                        className="submit_form_control"
                        value={company.city}
                        readOnly
                      />
                    </Col>
                    <Col xs="3" className="custom_col_submit mb-4">
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        placeholder="Enter State"
                        className="submit_form_control"
                        value={company.state}
                        readOnly
                      />
                    </Col>
                    <Col xs="6" className="custom_col_submit mb-4">
                      <Form.Label>Country</Form.Label>
                      <Select
                        className="submit_form_select"
                        classNamePrefix="react-select"
                        options={countryOptions}
                        value={countryOptions.find((o) => o.value === company.country) || null}
                        isDisabled
                        isSearchable={false}
                        placeholder="Select Country"
                        styles={readOnlySelectStyles}
                        formatOptionLabel={({ countryCode, label }) => (
                          <div className="d-flex align-items-center">
                            {countryCode && (
                              <img
                                src={`https://flagcdn.com/w20/${String(countryCode).toLowerCase()}.png`}
                                alt=""
                                className="me-2"
                                style={{ width: "20px", height: "auto" }}
                              />
                            )}
                            <span>{label}</span>
                          </div>
                        )}
                      />
                    </Col>
                  </Row>
                </>
              )}

              {/* ── Note for Editor ── */}
              <Row>
                <Col xs="12">
                  <Form.Group className="mb-4">
                    <Form.Label>Note for editor</Form.Label>
                    <Form.Control
                      className="submit_form_select"
                      placeholder="No note provided"
                      value={formData.addNote}
                      as="textarea"
                      rows={8}
                      readOnly
                    />
                  </Form.Group>
                </Col>
              </Row>

            </Form>
          </div>

          {/* ── No action buttons — read only view ── */}
        </div>
      </div>
    </>
  );
}
